import {
  APP_PATHS,
  EMPLOYEE_ATTENDANCE_TABS,
  MY_REVIEWS_TABS,
  MY_TIME_OFF_TABS,
  SERVICE_REQUEST_TYPES,
  TEAM_ATTENDANCE_TABS,
  TEAM_TIME_OFF_TABS,
} from '@/constant/app-paths';
import { SKILL_MANAGER_TABS } from '@/modules/settings';
import { Navigate, Route, generatePath, useLocation, useParams } from 'react-router-dom';

const SR = APP_PATHS.SERVICE_REQUESTS;
const { NOC, BYOD, BUSINESS_CARD, ID_CARD, INSURANCE } = SERVICE_REQUEST_TYPES;

/**
 * Every URL the app had before the menu followed the Navigation Map, and where it is now. Notifications,
 * e-mails and bookmarks already sent still hold these, so they must keep working: never remove a row.
 * `:param`s are carried over by name; a `?query` on the target is added to the one the link had.
 */
export const LEGACY_REDIRECTS: ReadonlyArray<readonly [from: string, to: string]> = [
  ['/profile', APP_PATHS.MY_PROFILE],
  ['/profile/edit', APP_PATHS.MY_PROFILE_EDIT],
  ['/logs', APP_PATHS.AUDIT_LOG],

  ['/hr/directory', APP_PATHS.PEOPLE],
  ['/hr/directory/new/create', APP_PATHS.PEOPLE_NEW],
  ['/hr/directory/:employeeId', `${APP_PATHS.PEOPLE}/:employeeId`],
  ['/hr/directory/:employeeId/create', `${APP_PATHS.PEOPLE}/:employeeId/edit`],

  ['/hr/my-timesheet', APP_PATHS.TIME_TRACKER],
  ['/hr/my-attendance', APP_PATHS.ATTENDANCE],
  ['/hr/manage-attendance', APP_PATHS.TEAM_ATTENDANCE],
  ['/hr/manage-attendance/:userId', `${APP_PATHS.TEAM_ATTENDANCE}/:userId`],
  ['/hr/timesheet-manager', APP_PATHS.TEAM_TIMESHEETS],
  ['/hr/timesheet-manager/:userId', `${APP_PATHS.TEAM_TIMESHEETS}/:userId`],
  ['/hr/overtime-manager', `${APP_PATHS.TEAM_ATTENDANCE}?tab=${TEAM_ATTENDANCE_TABS.OVERTIME}`],
  [
    '/hr/overtime-manager/:userId',
    `${APP_PATHS.TEAM_ATTENDANCE}/:userId?tab=${EMPLOYEE_ATTENDANCE_TABS.OVERTIME}`,
  ],
  // The Overtime page (Phase 0) became the Overtime tab of Team Attendance in Phase 1.
  [
    '/time-attendance/overtime',
    `${APP_PATHS.TEAM_ATTENDANCE}?tab=${TEAM_ATTENDANCE_TABS.OVERTIME}`,
  ],
  [
    '/time-attendance/overtime/:userId',
    `${APP_PATHS.TEAM_ATTENDANCE}/:userId?tab=${EMPLOYEE_ATTENDANCE_TABS.OVERTIME}`,
  ],

  ['/hr/my-leave', APP_PATHS.MY_TIME_OFF],
  ['/hr/my-toil', `${APP_PATHS.MY_TIME_OFF}?tab=${MY_TIME_OFF_TABS.TOIL}`],
  ['/hr/leave-manager', APP_PATHS.TEAM_TIME_OFF],
  ['/hr/toil-manager', `${APP_PATHS.TEAM_TIME_OFF}?tab=${TEAM_TIME_OFF_TABS.TOIL}`],
  // The TOIL pages (Phase 0) became the TOIL Requests tabs of the Time Off pages in Phase 2.
  ['/time-off/my-toil', `${APP_PATHS.MY_TIME_OFF}?tab=${MY_TIME_OFF_TABS.TOIL}`],
  ['/time-off/team-toil', `${APP_PATHS.TEAM_TIME_OFF}?tab=${TEAM_TIME_OFF_TABS.TOIL}`],
  ['/hr/leave-calendar', APP_PATHS.TIME_OFF_CALENDAR],
  ['/hr/sick-leave', APP_PATHS.SICK_LEAVE],

  ['/hr/peer-requests', `${APP_PATHS.MY_REVIEWS}?tab=${MY_REVIEWS_TABS.REQUESTS}`],
  ['/hr/my-submissions', `${APP_PATHS.MY_REVIEWS}?tab=${MY_REVIEWS_TABS.SUBMISSIONS}`],
  ['/hr/my-submissions/:pmReviewDetailId', `${APP_PATHS.MY_REVIEW_SUBMISSIONS}/:pmReviewDetailId`],
  ['/hr/reviews', APP_PATHS.ALL_REVIEWS],
  ['/hr/reviews/:reviewId', `${APP_PATHS.ALL_REVIEWS}/:reviewId`],
  // The dashboard opens from a review's details now (Phase 6).
  ['/hr/performance-dashboard', APP_PATHS.ALL_REVIEWS],

  ['/hr/potential-profiles', APP_PATHS.CANDIDATES],
  ['/hr/potential-profiles/builder', APP_PATHS.CANDIDATES_NEW],
  ['/hr/conditional-offers', APP_PATHS.OFFERS],
  ['/hr/conditional-offers/builder', APP_PATHS.OFFERS_NEW],

  ['/hr/surveys', APP_PATHS.ALL_SURVEYS],
  ['/hr/surveys/:batchId', `${APP_PATHS.ALL_SURVEYS}/:batchId`],
  ['/hr/surveys/:batchId/:submissionId', `${APP_PATHS.ALL_SURVEYS}/:batchId/:submissionId`],
  ['/hr/survey-my-submissions', APP_PATHS.MY_SURVEY_RESPONSES],
  ['/hr/survey-my-submissions/:submissionId', `${APP_PATHS.MY_SURVEY_RESPONSES}/:submissionId`],

  ['/hr/request-lunch', APP_PATHS.ORDER_MEAL],
  ['/hr/manage-lunch', APP_PATHS.MEAL_ORDERS],

  ['/hr/service-desk', SR],
  ['/hr/service-desk/noc', `${SR}?type=${NOC}`],
  ['/hr/service-desk/noc/apply', `${SR}/${NOC}/new`],
  ['/hr/service-desk/noc/report/:id', `${SR}/${NOC}/:id/report`],
  ['/hr/service-desk/byod', `${SR}?type=${BYOD}`],
  ['/hr/service-desk/byod/apply', `${SR}/${BYOD}/new`],
  ['/hr/service-desk/business-card', `${SR}?type=${BUSINESS_CARD}`],
  ['/hr/service-desk/id-card', `${SR}?type=${ID_CARD}`],
  ['/hr/service-desk/id-card/apply', `${SR}/${ID_CARD}/new`],
  ['/hr/service-desk/insurance', `${SR}?type=${INSURANCE}`],
  ['/hr/service-desk/insurance/apply', `${SR}/${INSURANCE}/new`],

  // The per-type list pages (Phase 0) became the type picker of Service Requests in Phase 3.
  [`${SR}/${NOC}`, `${SR}?type=${NOC}`],
  [`${SR}/${BYOD}`, `${SR}?type=${BYOD}`],
  [`${SR}/${BUSINESS_CARD}`, `${SR}?type=${BUSINESS_CARD}`],
  [`${SR}/${ID_CARD}`, `${SR}?type=${ID_CARD}`],
  [`${SR}/${INSURANCE}`, `${SR}?type=${INSURANCE}`],

  ['/hr/department-manager', APP_PATHS.DEPARTMENTS],
  ['/hr/designation-manager', APP_PATHS.DESIGNATIONS],
  ['/hr/location-manager', APP_PATHS.LOCATIONS],
  ['/hr/project-manager', APP_PATHS.PROJECTS],
  ['/hr/mbm-project-manager', APP_PATHS.MBM_PROJECTS],
  ['/hr/leave-builder', APP_PATHS.LEAVE_POLICIES],
  ['/hr/holiday', APP_PATHS.HOLIDAYS],
  ['/hr/form-creation', APP_PATHS.REVIEW_TEMPLATES],
  ['/hr/form-creation/:formId', `${APP_PATHS.REVIEW_TEMPLATES}/:formId`],
  ['/hr/kpi-configuration', APP_PATHS.KPIS],
  ['/hr/skill-manager', APP_PATHS.SKILLS],
  ['/hr/skill-category-manager', `${APP_PATHS.SKILLS}?tab=${SKILL_MANAGER_TABS.CATEGORIES}`],
  [
    '/hr/skill-sub-category-manager',
    `${APP_PATHS.SKILLS}?tab=${SKILL_MANAGER_TABS.SUB_CATEGORIES}`,
  ],
  ['/hr/survey-form-creation', APP_PATHS.SURVEY_FORMS],
  ['/hr/survey-form-creation/:formId', `${APP_PATHS.SURVEY_FORMS}/:formId`],
  ['/hr/ctc-level-manager', APP_PATHS.CTC_LEVELS],
  ['/hr/noc-type-manager', APP_PATHS.NOC_TYPES],
  ['/hr/role-manager', APP_PATHS.ROLES],
];

/**
 * Sends an old URL to its new page: the same `:param`s, the link's `?query` (e.g. `?record=` of a
 * notification) plus the target's own, and the `#hash`.
 */
export const LegacyRedirect = ({ to }: { to: string }) => {
  const params = useParams();
  const { search, hash } = useLocation();
  const [pathPattern, targetQuery = ''] = to.split('?');
  const query = new URLSearchParams(search);
  new URLSearchParams(targetQuery).forEach((value, key) => {
    if (!query.has(key)) query.set(key, value);
  });
  const queryText = query.toString();

  return (
    <Navigate
      to={{
        pathname: generatePath(pathPattern, params),
        search: queryText ? `?${queryText}` : '',
        hash,
      }}
      replace
    />
  );
};

/** The redirect routes, for `<Routes>`. */
export const legacyRedirectRoutes = LEGACY_REDIRECTS.map(([from, to]) => (
  <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
));
